import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  IconButton,
  Card,
  Chip,
  Tab,
  Tabs,
  keyframes,
  Avatar,
} from '@mui/material';
import {
  ArrowBack,
  Description,
  ChatBubble,
  Gavel,
  Star,
  CheckCircle,
  AttachMoney,
  CalendarToday,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { axelionColors } from '../../theme/axelionTheme';
import clientService from '../../services/clientService';
import EmptyState from '../../components/UI/EmptyState';

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PortfolioPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [documents, setDocuments] = useState([]);
  const [consultations, setConsultations] = useState([]);
  const [aiConversations, setAiConversations] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadPortfolioData();
  }, []);

  const loadPortfolioData = async () => {
    try {
      setLoading(true);
      const [docsData, consData, aiData, favsData, statsData] = await Promise.all([
        clientService.documents.getDocuments(),
        clientService.consultations.getConsultations('all'),
        clientService.aiChat.getConversations(),
        clientService.favorites.getFavorites(),
        clientService.dashboard.getStats(),
      ]);

      setDocuments(docsData);
      setConsultations(consData);
      setAiConversations(aiData);
      setFavorites(favsData);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading portfolio:', error);
      toast.error('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: axelionColors.bgCream,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            border: `2px solid ${axelionColors.borderLight}`,
            borderTopColor: axelionColors.gold,
            animation: 'spin 1s linear infinite',
            '@keyframes spin': {
              '0%': { transform: 'rotate(0deg)' },
              '100%': { transform: 'rotate(360deg)' },
            },
          }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: axelionColors.bgCream, pb: 6 }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: axelionColors.bgLight,
          borderBottom: `1px solid ${axelionColors.borderLight}`,
          py: { xs: 2, md: 4 },
          px: { xs: 1, md: 2 },
        }}
      >
        <Container maxWidth="xl">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 3 }, mb: 3 }}>
            <IconButton
              onClick={() => navigate('/dashboard')}
              sx={{
                border: `1px solid ${axelionColors.borderLight}`,
                bgcolor: axelionColors.bgLight,
                borderRadius: '8px',
                '&:hover': {
                  bgcolor: axelionColors.bgWarm,
                  borderColor: axelionColors.gold,
                },
              }}
            >
              <ArrowBack sx={{ color: axelionColors.textDark }} />
            </IconButton>
            <Box>
              <Typography
                sx={{
                  fontWeight: 300,
                  fontSize: { xs: '1.25rem', md: '1.75rem' },
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  color: axelionColors.textDark,
                  mb: 0.5,
                }}
              >
                Юридическое досье
              </Typography>
              <Typography sx={{ color: axelionColors.textMuted, fontSize: '0.9rem' }}>
                Вся ваша история на платформе
              </Typography>
            </Box>
          </Box>

          {/* Stats Overview */}
          <Grid container spacing={2}>
            {[
              {
                icon: <Description sx={{ fontSize: 24 }} />,
                label: 'Документы',
                value: documents.length,
                color: axelionColors.gold,
              },
              {
                icon: <Gavel sx={{ fontSize: 24 }} />,
                label: 'Консультации',
                value: consultations.length,
                color: axelionColors.bronze,
              },
              {
                icon: <ChatBubble sx={{ fontSize: 24 }} />,
                label: 'AI-чаты',
                value: aiConversations.length,
                color: axelionColors.success,
              },
              {
                icon: <Star sx={{ fontSize: 24 }} />,
                label: 'Избранные',
                value: favorites.length,
                color: axelionColors.warning,
              },
            ].map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <Card
                  sx={{
                    p: 2,
                    bgcolor: axelionColors.bgLight,
                    border: `1px solid ${axelionColors.borderLight}`,
                    borderRadius: '8px',
                    textAlign: 'center',
                  }}
                >
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '8px',
                      bgcolor: `${stat.color}20`,
                      color: stat.color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 1,
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '1.5rem',
                      fontWeight: 300,
                      color: axelionColors.textDark,
                      mb: 0.5,
                    }}
                  >
                    {stat.value}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.7rem',
                      color: axelionColors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.1em',
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 4 }}>
        {/* Tabs */}
        <Card
          sx={{
            mb: 3,
            bgcolor: axelionColors.bgLight,
            border: `1px solid ${axelionColors.borderLight}`,
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(26, 26, 26, 0.04)',
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(e, value) => setActiveTab(value)}
            sx={{
              borderBottom: `1px solid ${axelionColors.borderLight}`,
              '& .MuiTab-root': {
                color: axelionColors.textMuted,
                fontWeight: 500,
                fontSize: '0.8rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                '&.Mui-selected': {
                  color: axelionColors.gold,
                },
              },
              '& .MuiTabs-indicator': {
                bgcolor: axelionColors.gold,
                height: 2,
              },
            }}
          >
            <Tab label="Документы" />
            <Tab label="Консультации" />
            <Tab label="AI-чаты" />
            <Tab label="Избранные" />
          </Tabs>
        </Card>

        {/* Tab Content */}
        {/* Documents Tab */}
        {activeTab === 0 && (
          <Box>
            {documents.length === 0 ? (
              <EmptyState
                icon={<Description sx={{ fontSize: 64, color: axelionColors.borderLight }} />}
                title="Нет документов"
                subtitle="Загрузите первый документ для AI-анализа"
                actionLabel="Загрузить документ"
                onAction={() => navigate('/documents')}
              />
            ) : (
              <Grid container spacing={2}>
                {documents.map((doc, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={doc.id}
                    sx={{
                      animation: `${fadeInUp} 0.5s ease-out`,
                      animationDelay: `${index * 0.05}s`,
                      animationFillMode: 'both',
                    }}
                  >
                    <Card
                      onClick={() => navigate('/documents')}
                      sx={{
                        p: 2,
                        bgcolor: axelionColors.bgLight,
                        border: `1px solid ${axelionColors.borderLight}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: axelionColors.gold,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '8px',
                            bgcolor: axelionColors.accentLight,
                            color: axelionColors.gold,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Description />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.9rem',
                              color: axelionColors.textDark,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {doc.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: axelionColors.textMuted,
                            }}
                          >
                            {new Date(doc.createdAt).toLocaleDateString('ru-RU')}
                          </Typography>
                        </Box>
                        {doc.aiAnalysis && (
                          <Chip
                            icon={<CheckCircle sx={{ fontSize: 14 }} />}
                            label="AI"
                            size="small"
                            sx={{
                              bgcolor: axelionColors.successLight,
                              color: axelionColors.success,
                              fontWeight: 500,
                              fontSize: '0.7rem',
                            }}
                          />
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Consultations Tab */}
        {activeTab === 1 && (
          <Box>
            {consultations.length === 0 ? (
              <EmptyState
                icon={<Gavel sx={{ fontSize: 64, color: axelionColors.borderLight }} />}
                title="Нет консультаций"
                subtitle="Забронируйте первую консультацию с юристом"
                actionLabel="Найти юриста"
                onAction={() => navigate('/lawyers')}
              />
            ) : (
              <Grid container spacing={2}>
                {consultations.map((cons, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    key={cons.id}
                    sx={{
                      animation: `${fadeInUp} 0.5s ease-out`,
                      animationDelay: `${index * 0.05}s`,
                      animationFillMode: 'both',
                    }}
                  >
                    <Card
                      onClick={() => navigate('/consultations')}
                      sx={{
                        p: 2,
                        bgcolor: axelionColors.bgLight,
                        border: `1px solid ${axelionColors.borderLight}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: axelionColors.gold,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: axelionColors.bgBeige,
                            color: axelionColors.gold,
                          }}
                        >
                          {cons.lawyerName?.charAt(0) || 'Ю'}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.9rem',
                              color: axelionColors.textDark,
                            }}
                          >
                            {cons.lawyerName || 'Юрист'}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: axelionColors.textMuted,
                            }}
                          >
                            {cons.topic || 'Консультация'}
                          </Typography>
                        </Box>
                        <Chip
                          label={cons.status === 'completed' ? 'Завершена' : 'Активна'}
                          size="small"
                          sx={{
                            bgcolor:
                              cons.status === 'completed'
                                ? axelionColors.successLight
                                : axelionColors.accentLight,
                            color:
                              cons.status === 'completed'
                                ? axelionColors.success
                                : axelionColors.gold,
                            fontWeight: 500,
                            fontSize: '0.65rem',
                          }}
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, fontSize: '0.75rem', color: axelionColors.textMuted }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CalendarToday sx={{ fontSize: 14 }} />
                          {new Date(cons.createdAt).toLocaleDateString('ru-RU')}
                        </Box>
                        {cons.price > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <AttachMoney sx={{ fontSize: 14 }} />
                            {cons.price.toLocaleString()} сум
                          </Box>
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* AI Chats Tab */}
        {activeTab === 2 && (
          <Box>
            {aiConversations.length === 0 ? (
              <EmptyState
                icon={<ChatBubble sx={{ fontSize: 64, color: axelionColors.borderLight }} />}
                title="Нет AI-чатов"
                subtitle="Начните первый диалог с AI-консультантом"
                actionLabel="Открыть AI-чат"
                onAction={() => navigate('/ai-chat')}
              />
            ) : (
              <Grid container spacing={2}>
                {aiConversations.map((conv, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={conv.id}
                    sx={{
                      animation: `${fadeInUp} 0.5s ease-out`,
                      animationDelay: `${index * 0.05}s`,
                      animationFillMode: 'both',
                    }}
                  >
                    <Card
                      onClick={() => navigate('/ai-chat')}
                      sx={{
                        p: 2,
                        bgcolor: axelionColors.bgLight,
                        border: `1px solid ${axelionColors.borderLight}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: axelionColors.gold,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: '8px',
                            bgcolor: axelionColors.accentLight,
                            color: axelionColors.gold,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <ChatBubble />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.9rem',
                              color: axelionColors.textDark,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {conv.title || 'AI-консультация'}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: '0.75rem',
                              color: axelionColors.textMuted,
                            }}
                          >
                            {new Date(conv.createdAt).toLocaleDateString('ru-RU')}
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* Favorites Tab */}
        {activeTab === 3 && (
          <Box>
            {favorites.length === 0 ? (
              <EmptyState
                icon={<Star sx={{ fontSize: 64, color: axelionColors.borderLight }} />}
                title="Нет избранных юристов"
                subtitle="Добавьте юристов в избранное для быстрого доступа"
                actionLabel="Найти юриста"
                onAction={() => navigate('/lawyers')}
              />
            ) : (
              <Grid container spacing={2}>
                {favorites.map((lawyer, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={lawyer.id}
                    sx={{
                      animation: `${fadeInUp} 0.5s ease-out`,
                      animationDelay: `${index * 0.05}s`,
                      animationFillMode: 'both',
                    }}
                  >
                    <Card
                      onClick={() => navigate(`/lawyers/${lawyer.id}`)}
                      sx={{
                        p: 2,
                        bgcolor: axelionColors.bgLight,
                        border: `1px solid ${axelionColors.borderLight}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          borderColor: axelionColors.gold,
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          src={lawyer.avatar}
                          sx={{
                            width: 48,
                            height: 48,
                            border: `2px solid ${axelionColors.gold}`,
                            bgcolor: axelionColors.bgBeige,
                            color: axelionColors.gold,
                          }}
                        >
                          {lawyer.name?.charAt(0)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontWeight: 500,
                              fontSize: '0.9rem',
                              color: axelionColors.textDark,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {lawyer.name}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Star sx={{ fontSize: 14, color: axelionColors.gold }} />
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: axelionColors.textMuted,
                              }}
                            >
                              {lawyer.rating || 0}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default PortfolioPage;
